import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { ChevronDown, CircleUser, Languages, LogOut, Monitor, Moon, Palette, Sun } from "lucide-react"
// The route tree is imported directly: this component is part of the layout the router
// itself pulls in, so reaching for the router barrel here would close an import cycle.
import { routeTree } from "@/app/router/route-tree"
import { useLogoutMutation, useSessionQuery } from "@/entities/session/queries"
import { APP_VERSION } from "@/shared/constants"
import type { DropdownMenuOptions } from "@/shared/components/menu/DropdownMenu"
import DropdownMenu from "@/shared/components/menu/DropdownMenu"
import { useTheme } from "@/shared/hooks/useTheme"
import i18n from "@/shared/i18n"
import { Button } from "@/shadcn/components/ui/button"

export default function UserMenuButton() {
  const { t } = useTranslation(["auth", "common"])
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const { data: session } = useSessionQuery()
  const logoutMutation = useLogoutMutation()

  const MENU_CLOSE_DURATION = 200

  const handleLogout = async () => {
    await logoutMutation.mutateAsync()
    await navigate(routeTree.auth.path(), { replace: true })
  }

  const options: DropdownMenuOptions = {
    contentProps: { align: "end", sideOffset: 10 },
    items: [
      {
        type: "submenu",
        label: t("common:theme.label"),
        icon: Palette,
        items: [
          {
            type: "action",
            label: t("common:theme.light"),
            icon: Sun,
            active: theme === "light",
            onSelect: () => setTimeout(() => setTheme("light"), MENU_CLOSE_DURATION),
          },
          {
            type: "action",
            icon: Moon,
            label: t("common:theme.dark"),
            active: theme === "dark",
            onSelect: () => setTimeout(() => setTheme("dark"), MENU_CLOSE_DURATION),
          },
          {
            type: "action",
            icon: Monitor,
            label: t("common:theme.system"),
            active: theme === "system",
            onSelect: () => setTimeout(() => setTheme("system"), MENU_CLOSE_DURATION),
          },
        ],
      },
      {
        type: "submenu",
        label: t("common:appLanguage"),
        icon: Languages,
        items: [
          {
            type: "action",
            label: "English",
            active: i18n.language === "en",
            onSelect: () => setTimeout(() => i18n.changeLanguage("en"), MENU_CLOSE_DURATION),
          },
          {
            type: "action",
            label: "Русский",
            active: i18n.language === "ru",
            onSelect: () => setTimeout(() => i18n.changeLanguage("ru"), MENU_CLOSE_DURATION),
          },
        ],
      },
      ...(session
        ? ([
            { type: "separator" },
            {
              type: "action",
              label: t("auth:logout"),
              icon: LogOut,
              onSelect: () => setTimeout(() => void handleLogout(), MENU_CLOSE_DURATION),
            },
          ] satisfies DropdownMenuOptions["items"])
        : []),
      { type: "separator" },
      {
        type: "label",
        label: <div className="text-center text-foreground">{t("common:appVersion", { version: APP_VERSION })}</div>,
      },
    ],
  }

  const Trigger = (
    <Button variant="ghost" className="max-w-56 text-foreground">
      <CircleUser className="size-4" />
      {session?.user.login ?? t("common:settings")}
      <ChevronDown className="size-4" />
    </Button>
  )

  return <DropdownMenu options={options} renderTrigger={() => Trigger} />
}
